import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import {
    List,
    Grid,
    Divider,
    Typography,
    Button,
    Alert,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BackDrop from '../../../shared/backdropDE';

import {
    getPendingNewslettersByEmployee,
    sendSignature,
} from './logic';
import { getUser } from '../../../shared/auth-service';
import { ACTIONS, TITLES } from '../../../shared/constants';
import ContentViewer from '../../content-viewer';
import UserAssessment from '../../user-assessment';
import UserAssessmentFeedBack from '../../user-assessment-feedback';

const PendingNewslettersSource = () => {
    const [data, setData] = useState([]); // Estado para almacenar los datos
    const [showQuestions, setShowQuestions] = useState(null);
    const [showFeedback, setShowFeedback] = useState(null);
    const [assessment, setAssessment] = useState(null);
    const [signatureId, setSignatureId] = useState(null);
    const [newsletterId, setNewsletterId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);

    const { enqueueSnackbar } = useSnackbar();
    const user = getUser();

    // Cargar datos al montar el componente
    useEffect(() => {
        const fetchData = async () => {
            if (loading) {
                try {
                
                    const result = await getPendingNewslettersByEmployee(user.employeeId);
                    setData(result);
                    setLoading(false);
                } catch (error) {
                    setLoading(false);
                    console.error('Error al cargar los datos:', error);
                    enqueueSnackbar('Error al cargar los datos.', { variant: 'error' });
                }
            }
          
        };
        fetchData();
    }, [user.employeeId, enqueueSnackbar]); // Dependencias

    const handleAcept = async (signatureId, resp) => {
        if (signatureId) {
            try {
                const accepted = await sendSignature(signatureId, '', user.employeeId);
                if (accepted) {
                    enqueueSnackbar('Circular aceptada', { variant: 'success' });
                    setData((prevData) =>
                        prevData.filter((item) => item.signatures[0].id !== signatureId)
                    ); // Actualiza los datos eliminando el aceptado
                }
            } catch (error) {
                console.error('Error al aceptar la circular:', error);
                enqueueSnackbar('Error al aceptar la circular.', { variant: 'error' });
            }
        } else {
            setShowFeedback(true);
            setMessage(resp.data);
        }
    };

    if (loading) {
        return (<BackDrop />);
    }

    const handleShowQuestions = (assessment, signatureId, id) => {
        setSignatureId(signatureId);
        setAssessment(assessment);
        setNewsletterId(id);
        setShowQuestions(true);
    };

    if (showFeedback) {
        return (
            <UserAssessmentFeedBack
                name={assessment.name}
                assessment={assessment}
                message={message}
                handleClose={() => setShowFeedback(false)}
                questions={assessment.questions}
                refresh={() => window.location.reload()}
            />
        );
    }

    if (showQuestions) {
        return (
            <UserAssessment
                questions={assessment.questions}
                assessmentId={assessment.id}
                name={assessment.name}
                handleAcept={handleAcept}
                signatureId={signatureId}
                assessment={assessment}
                newsletterId={newsletterId}
            />
        );
    }

    return (
        <>
            <Typography
                variant="h5"
                style={{ color: '#1a3072' }}
                fontWeight="bold"
                sx={{ flexGrow: 1, m: 1 }}
            >
                Mis <span style={{ color: '#00b0ef' }}>Circulares pendientes </span>
            </Typography>

            {data.length === 0 ? (
                // Mostrar mensaje cuando no hay más circulares
                <Alert severity="success" sx={{ mt: 2 }}>
                    ¡Estás al día! No tienes circulares pendientes.
                </Alert>
            ) : (
                // Mostrar la lista de circulares pendientes
                <List>
                    {data.map(({ name, newsletterContent, assessment, signatures, id, retries }) => {
                        const signatureId = signatures[0].id;
                        return (
                            <React.Fragment key={id}>
                                <Accordion>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="panel1a-content"
                                        id="panel1a-header"
                                    >
                                        <Typography gutterBottom variant="h6" component="div">
                                            <b>{name}</b>
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {newsletterContent.map((item, index) => (
                                            <ContentViewer key={index} {...item} />
                                        ))}
                                        {assessment && assessment.active && (
                                            <Grid container spacing={3} rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                                                <Grid item xs={12}>
                                                    <Typography gutterBottom variant="h8">
                                                        {TITLES.START_ASSESMENT}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    {retries < assessment.numberOfAttempts && (
                                                        <Button
                                                            onClick={() =>
                                                                handleShowQuestions(assessment, signatureId, id)
                                                            }
                                                            variant="contained"
                                                            color="info"
                                                            endIcon={<CheckIcon />}
                                                        >
                                                            {ACTIONS.START_ASSESMENT}
                                                        </Button>
                                                    )}
                                                    {retries >= assessment.numberOfAttempts && (
                                                        <Typography gutterBottom variant="h8">
                                                            <span style={{ color: '#00b0ef' }}>
                                                                {TITLES.RETRY_TOMORROW} {retries} intentos
                                                            </span>
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        )}
                                        {(!assessment || !assessment?.active) && (
                                            <Grid
                                                container
                                                spacing={12}
                                                rowSpacing={1}
                                                columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                                            >
                                                <Grid item xs={12}>
                                                    <Typography gutterBottom variant="h9">
                                                        {TITLES.MARK_NEWSLETTER_AS_COMPLETE}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Button
                                                        onClick={() => handleAcept(signatureId)}
                                                        variant="contained"
                                                        color="info"
                                                        endIcon={<CheckIcon />}
                                                    >
                                                        {ACTIONS.MARK_NEWSLETTER_AS_COMPLETE}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                                <Divider light />
                            </React.Fragment>
                        );
                    })}
                </List>
            )}
        </>
    );
};

export default PendingNewslettersSource;
